import { getLocalizedStringValue } from '@griply/utils';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconFormIcons,
  IconFormNames,
} from 'shared/assets/icons/icon-form-icons';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { Checkbox } from 'shared/components/ui/checkbox';
import { Icon } from 'shared/components/ui/icon';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { PageEvents } from 'shared/services/tracking';
import { ID } from 'shared/types/id';

import { lifeAreas } from '../constants';
import { OnboardingHeader } from '../onboarding-header';
import { OnboardingSideImage } from '../onboarding-side-image';
import { ProgressBar } from '../progress-bar';
import * as Styled from './life-areas.style';

const LIFE_AREAS_IMAGE_URL = '/images/onboarding/life-areas.png';

export type LifeAreasProps = {
  nextStep: () => void;
  prevStep: () => void;
  currentStep: number;
  previousStep: number;
  totalSteps: number;
  selectedLifeAreaTemplateIds: ID[];
  onSubmit: (selectedIds: ID[]) => void;
};

export const LifeAreas: React.FC<LifeAreasProps> = ({
  nextStep,
  prevStep,
  currentStep,
  previousStep,
  totalSteps,
  selectedLifeAreaTemplateIds,
  onSubmit,
}) => {
  useTrackPageVisitEvent(PageEvents.OnboardingLifeAreas, undefined);
  const { t } = useTranslation();
  const locale = useI18nLocale();
  const [selectedIds, setSelectedIds] = useState<ID[]>(
    selectedLifeAreaTemplateIds,
  );

  // Sync internal state with prop
  useEffect(() => {
    setSelectedIds(selectedLifeAreaTemplateIds);
  }, [selectedLifeAreaTemplateIds]);

  const toggleSelection = (id: ID) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(selectedIds);
    nextStep();
  };

  const handleSkip = () => {
    nextStep();
  };

  return (
    <Styled.OnboardingContainer>
      <Styled.LeftPanel>
        <OnboardingHeader onBack={prevStep} />

        <Styled.LeftPanelContent>
          <ProgressBar
            current={currentStep}
            previous={previousStep}
            total={totalSteps}
          />

          <Styled.Form id="life-areas-form" onSubmit={handleSubmit}>
            <Typography variant="h4" component="h1">
              {t('forms.onboarding.life-areas.title')}
            </Typography>
            <Styled.Subtitle>
              {t('forms.onboarding.life-areas.subtitle')}
            </Styled.Subtitle>

            <Styled.List>
              {lifeAreas.map((lifeArea) => {
                const isActive = selectedIds.includes(lifeArea.id);
                const IconComponent =
                  IconFormIcons[
                    lifeArea.iconName ?? IconFormNames.PlaceholderIcon
                  ] ?? IconFormIcons[IconFormNames.PlaceholderIcon];
                return (
                  <Styled.ListItem
                    $active={isActive}
                    key={lifeArea.id}
                    onClick={() => toggleSelection(lifeArea.id)}
                  >
                    <Styled.ListItemContent>
                      <Styled.ListItemIcon $color={lifeArea.color}>
                        <Icon icon={IconComponent} />
                      </Styled.ListItemIcon>
                      <Typography variant="subtitle2">
                        {getLocalizedStringValue(lifeArea.name, locale)}
                      </Typography>
                    </Styled.ListItemContent>

                    <Styled.CheckboxContainer>
                      <Checkbox readOnly checked={isActive} />
                    </Styled.CheckboxContainer>
                  </Styled.ListItem>
                );
              })}
            </Styled.List>
          </Styled.Form>

          <Styled.FooterArea>
            <Button
              type="submit"
              form="life-areas-form"
              disabled={!selectedIds.length}
              size={Sizes.Medium}
              fullWidth
              alignCenter
            >
              {t('forms.onboarding.continue.label')}
            </Button>
            <Button
              type="button"
              onClick={handleSkip}
              variant={Variants.Text}
              size={Sizes.Medium}
              color={Colors.Inherit}
              fullWidth
              alignCenter
            >
              {t('forms.onboarding.skip.label')}
            </Button>
          </Styled.FooterArea>
        </Styled.LeftPanelContent>
      </Styled.LeftPanel>
      <Styled.RightPanelWrapper>
        <OnboardingSideImage
          imageUrl={LIFE_AREAS_IMAGE_URL}
          maxImageWidth={598}
        />
      </Styled.RightPanelWrapper>
    </Styled.OnboardingContainer>
  );
};
